import { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

export default function TrialBanner({ trialEnd }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!trialEnd) return;

    const calcRemaining = () => {
      const now = new Date();
      const end = new Date(trialEnd);
      const diff = Math.max(0, end - now);

      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      return { days, hours, minutes, seconds, total: diff };
    };

    setRemaining(calcRemaining());
    const interval = setInterval(() => setRemaining(calcRemaining()), 1000);
    return () => clearInterval(interval);
  }, [trialEnd]);

  if (!remaining) return null;

  const isUrgent = remaining.days <= 1;
  const isWarning = remaining.days <= 3 && !isUrgent;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-2xl border px-4 py-3 sm:px-5 sm:py-4 ${
        isUrgent
          ? 'bg-red-50 border-red-200'
          : isWarning
          ? 'bg-amber-50 border-amber-200'
          : 'bg-[#FFF7ED] border-orange-200'
      }`}
      data-testid="trial-banner"
    >
      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isUrgent ? 'bg-red-100' : isWarning ? 'bg-amber-100' : 'bg-[#C2410C]/10'
        }`}>
          {isUrgent ? (
            <AlertTriangle className="w-4.5 h-4.5 text-red-600" />
          ) : (
            <Clock className={`w-4.5 h-4.5 ${isWarning ? 'text-amber-600' : 'text-[#C2410C]'}`} />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${
            isUrgent ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-[#C2410C]'
          }`}>
            {isUrgent ? 'Trial ending soon!' : 'Free Trial'}
          </p>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            {remaining.total === 0
              ? 'Your trial has ended'
              : `${remaining.days}d ${remaining.hours}h ${remaining.minutes}m remaining`
            }
          </p>
        </div>

        {/* Countdown boxes */}
        <div className="flex items-center gap-1.5" data-testid="trial-countdown">
          {[
            { value: remaining.days, label: 'D' },
            { value: remaining.hours, label: 'H' },
            { value: remaining.minutes, label: 'M' },
            { value: remaining.seconds, label: 'S' },
          ].map((unit) => (
            <div
              key={unit.label}
              className={`flex flex-col items-center px-2 py-1.5 rounded-lg min-w-[38px] ${
                isUrgent ? 'bg-red-100' : isWarning ? 'bg-amber-100' : 'bg-white'
              }`}
            >
              <span className={`text-sm font-bold font-[monospace] leading-none ${
                isUrgent ? 'text-red-700' : isWarning ? 'text-amber-700' : 'text-[#1F2937]'
              }`}>
                {String(unit.value).padStart(2, '0')}
              </span>
              <span className="text-[9px] text-[#9CA3AF] mt-0.5">{unit.label}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
